import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import ReviewModeBanner from '../components/ReviewModeBanner';
import TeamRegistrationPanel from '../components/teams/TeamRegistrationPanel';
import { payOrder } from '../utils/payOrder';
import { loadRazorpayScript, buildRazorpayPrefill, withRazorpayCheckoutConfig, pollRazorpayOrderStatus } from '../utils/razorpay';
import { useAuth } from '../context/AuthContext';
import useProfileGate from '../hooks/useProfileGate';
import SportProfileFields, { missingSportFields, useSportSchema } from '../components/sports/SportProfileFields';
import { uploadToCloudinary } from '../lib/uploads';
import { isValidIndianMobile } from '../lib/phone';
import { instagramUrl } from '../lib/instagram';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B3A67]/30 bg-white';
const labelCls = 'block text-xs font-bold text-gray-500 mb-1';
const selectCls = inputCls;

function Field({ label, required, children }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

function SectionTitle({ icon, children }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-gray-100 mb-4">
      <span className="text-lg">{icon}</span>
      <h3 className="text-sm font-extrabold text-[#2B3A67] uppercase tracking-wider">{children}</h3>
    </div>
  );
}

// When the tournament uses "bring your own team", people register a whole team
// (players join through the team's invite link) instead of as individuals.
const TEAM_ROLE = { id: 'team', label: 'Register a Team', desc: 'Bring your own team — invite your players with a link', icon: 'ph-bold ph-users-three' };

const ROLES = [
  { id: 'player', label: 'Player', desc: 'Register as a cricket player', icon: 'ph-bold ph-user' },
  { id: 'team_owner', label: 'Team Owner', desc: 'Register your team for the tournament', icon: 'ph-bold ph-trophy' },
  { id: 'sponsor', label: 'Sponsor', desc: 'Sponsor the tournament or a team', icon: 'ph-bold ph-handshake' },
];

// Calculate age from date of birth
function calculateAge(dob) {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Universal-profile fields from `needs` this user hasn't filled in yet
function missingProfile(user, needs = []) {
  const have = {
    mobile_number: isValidIndianMobile(user?.mobile_number),
    date_of_birth: !!user?.date_of_birth,
    profile_picture: !!user?.profile_picture_url,
  };
  return needs.filter((n) => !have[n]);
}

const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

// Every day of the event (for the availability picker); null if too long/unknown
function eventDays(span) {
  if (!span?.start || !span?.end) return null;
  const days = [];
  // Work in UTC so the ISO date strings don't shift with the local timezone
  const d = new Date(`${span.start}T00:00:00Z`);
  const end = new Date(`${span.end}T00:00:00Z`);
  while (d <= end && days.length <= 31) {
    days.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return days.length > 31 ? null : days;
}

// Format date for display
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function TournamentRegisterPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const [invite, setInvite] = useState(null);   // team being joined via an invite link
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('');
  const [formData, setFormData] = useState({});
  const [declarations, setDeclarations] = useState({ accurate_rules_terms: false, media_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const { user, refreshUser } = useAuth();
  const gate = useProfileGate();
  const [draftLoaded, setDraftLoaded] = useState(false);
  const sportSpec = useSportSchema(info?.sport || 'cricket');

  const loadInfo = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/registration-info`);
      setInfo(res.data);
    } catch {
      toast.error('Failed to load event info');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fresh copy of the profile (it's shown read-only on the form)
  useEffect(() => { refreshUser().catch(() => {}); }, [refreshUser]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  // Only one registration open: select it straight away
  useEffect(() => {
    if (!info || inviteCode || role) return;
    const offeredRoles = info.roles_offered || ['player', 'team_owner', 'sponsor'];
    const open = info.team_formation_method === 'team_registration'
      ? ['team', ...(offeredRoles.includes('sponsor') ? ['sponsor'] : [])] : offeredRoles;
    const mine = info.my_roles?.[open[0]];
    if (open.length === 1 && !(mine && mine !== 'draft')) setRole(open[0]);
  }, [info, inviteCode, role]);

  // ?role=team (the "My team" link) opens the team dashboard directly
  const presetRole = searchParams.get('role');
  useEffect(() => {
    if (!inviteCode && presetRole === 'team' && info?.team_formation_method === 'team_registration') setRole('team');
  }, [inviteCode, presetRole, info]);

  // Invite link: load the team and lock the role to Player
  useEffect(() => {
    if (!inviteCode) return;
    axiosInstance.get(`/v1/events/${eventId}/team-invites/${inviteCode}`)
      .then((res) => { setInvite(res.data); setRole('player'); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'This invite link is not valid'));
  }, [eventId, inviteCode]);

  // Load draft when role changes; otherwise pre-fill a player's form from
  // their saved sport profile (e.g. cricket) from earlier registrations
  useEffect(() => {
    const loadDraft = async () => {
      if (!role || draftLoaded || !info) return;
      let loaded = false;
      try {
        const res = await axiosInstance.get(`/v1/events/${eventId}/register/draft/${role}`);
        if (res.data.draft && res.data.draft.registration_data) {
          setFormData(res.data.draft.registration_data);
          loaded = true;
          toast.info('Draft loaded');
        }
      } catch {
        // No draft found, that's ok
      }
      if (!loaded && role === 'player' && info.sport) {
        try {
          const prof = (await axiosInstance.get(`/auth/sport-profiles/${info.sport}`)).data;
          if (prof?.data && Object.keys(prof.data).length) {
            setFormData((f) => ({ ...prof.data, ...f }));
            toast.info('Filled in from your saved profile — check it\'s still right');
          }
        } catch { /* no saved profile */ }
      }
      setDraftLoaded(true);
    };
    loadDraft();
  }, [role, eventId, draftLoaded, info]);

  const set = (key, val) => setFormData(f => ({ ...f, [key]: val }));

  // What this registration needs from the universal profile (see backend
  // services/profile_requirements.py) and what's still missing
  const needsFor = (r) => info?.profile_requirements?.[invite && r === 'player' ? 'team_player' : r]?.needs || [];
  const askForDetails = (r, missing) => gate.ask(missing, {
    reason: 'Organisers need these to contact you and confirm your registration.',
    fields: [...needsFor(r), 'instagram_handle'],      // Instagram is optional
  });

  // Reset draft loaded state when role changes, and ask straight away for any
  // missing profile details (before they fill in the form)
  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setFormData({});
    setDraftLoaded(false);
    const missing = user ? missingProfile(user, needsFor(newRole)) : [];
    if (missing.length) askForDetails(newRole, missing);
  };

  // Upload file to Cloudinary with signed upload
  const uploadFile = async (file, fieldName, folder = 'registrations/id_proofs') => {
    setFileUploading(true);
    try {
      set(fieldName, await uploadToCloudinary(file, folder));
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed');
    } finally {
      setFileUploading(false);
    }
  };

  const allDeclared = declarations.accurate_rules_terms && declarations.media_consent;

  const feeForRole = (r) => {
    if (invite) return r === 'player' ? (invite.player_fee || 0) : 0;
    if (!info?.fees) return 0;
    return info.fees[r] || 0;
  };

  // Join a team through its invite link (pays the player fee first if the team chose per-player fees)
  const joinTeamViaInvite = async () => {
    let payment = {};
    if (invite.fee_mode === 'per_player' && invite.player_fee > 0) {
      const order = (await axiosInstance.post(`/v1/events/${eventId}/team-invites/${inviteCode}/payment-order`)).data;
      const paid = await payOrder({
        order,
        description: `${invite.event_title} — joining ${invite.team_name}`,
        prefill: { contact: user?.mobile_number, email: user?.email, name: user?.name },
      });
      payment = { razorpay_order_id: paid.order_id, razorpay_payment_id: paid.payment_id };
    }
    await axiosInstance.post(`/v1/events/${eventId}/team-invites/${inviteCode}/join`, {
      registration_data: formData, declaration_confirmed: true, declarations, ...payment,
    });
    toast.success(`You've joined ${invite.team_name}! The team's registrant will submit it for approval.`);
    navigate(`/tournaments/${eventId}?phase=register`);
  };

  // Save Draft
  const handleSaveDraft = async () => {
    if (!role) { toast.error('Select a role'); return; }
    
    setSavingDraft(true);
    try {
      await axiosInstance.post(`/v1/events/${eventId}/register/draft`, {
        role,
        registration_data: formData,
      });
      toast.success('Draft saved successfully');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // Client-side checks mirroring the backend (services/registration_forms.py)
  const formProblem = () => {
    if (role === 'player') {
      const missing = missingSportFields(sportSpec, formData);
      if (!['A', 'B', 'C', 'D'].includes(formData.category)) missing.push('Category');
      if (info.age_restricted && !formData.id_proof_url) missing.push('ID proof');
      if (formData.available_all_days === 'No' && Array.isArray(formData.unavailable_dates) && !formData.unavailable_dates.length) {
        missing.push("the days you can't make");
      }
      if (missing.length) return `Please fill in: ${missing.join(', ')}`;
    } else if (role === 'team_owner') {
      if (!formData.team_name?.trim() || !formData.ownership_type) return 'Fill in the team name and ownership type';
      if (formData.ownership_type === 'Corporate' && !GST_RE.test(formData.gst_number || '')) {
        return 'Enter a valid 15-character GST number for Corporate ownership';
      }
      if (formData.contact_is_different) {
        if (!formData.owner_name?.trim() || !formData.owner_phone?.trim() || !formData.owner_email?.trim()) {
          return "Fill in the contact person's name, phone and email";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) return 'Please enter a valid email address';
        if (!/^[\d\s+\-()]{10,16}$/.test(formData.owner_phone.trim())) return 'Please enter a valid phone number';
      }
    } else if (role === 'sponsor') {
      if (!formData.company_name?.trim()) return 'Company / brand name is required';
      if (!formData.sponsorship_type || !formData.budget_range) return 'Sponsorship type and budget range are required';
      if (formData.gst_number && !GST_RE.test(formData.gst_number)) return 'Enter a valid 15-character GST number (or leave it empty)';
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!role) { toast.error('Select a role'); return; }
    if (!allDeclared) { toast.error('Please confirm all declarations'); return; }
    const problem = formProblem();
    if (problem) { toast.error(problem); return; }
    // Missing profile details: ask now, before any payment
    const missing = missingProfile(user, needsFor(role));
    if (missing.length && !(await askForDetails(role, missing))) return;

    setSubmitting(true);
    if (invite) {
      try {
        await joinTeamViaInvite();
      } catch (e) {
        if (await gate.handleError(e)) toast.info('Thanks — tap Join again to finish.');
        else toast.error(e?.response?.data?.detail || (e?.message === 'Payment cancelled' ? 'Payment cancelled' : 'Could not join the team'));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    const fee = feeForRole(role);

    try {
      // Team Owner: Submit without payment (payment after admin approval)
      if (role === 'team_owner') {
        // Calculate GST for corporate
        const gstRate = formData.ownership_type === 'Corporate' ? 0.18 : 0;
        const gstAmount = Math.round(fee * gstRate);
        const totalFee = fee + gstAmount;
        
        await axiosInstance.post(`/v1/events/${eventId}/register`, {
          role,
          registration_data: {
            ...formData,
            calculated_gst_rate: gstRate,
            calculated_gst_amount: gstAmount,
            calculated_total_fee: totalFee,
          },
          declaration_confirmed: true,
          declarations,
          razorpay_order_id: '',
          razorpay_payment_id: '',
          razorpay_signature: '',
        });
        toast.success('Registration submitted! You will be notified to complete payment after admin approval.');
        navigate(-1);
        return;
      }

      // Player and Sponsor: Immediate payment flow
      if (fee > 0) {
        const orderRes = await axiosInstance.post(`/v1/events/${eventId}/registration-payment`, { role });
        const { order_id, amount, key_id } = orderRes.data;

        await loadRazorpayScript();

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        let paymentDone = false;
        let stopPolling = null;

        const submitRegistration = async ({ payment_id, signature }) => {
          await axiosInstance.post(`/v1/events/${eventId}/register`, {
            role,
            registration_data: formData,
            declaration_confirmed: true,
            declarations,
            razorpay_order_id: order_id,
            razorpay_payment_id: payment_id,
            razorpay_signature: signature || '',
          });
          toast.success(role === 'player' ? 'Registration confirmed!' : 'Registration submitted for approval!');
          navigate(-1);
        };

        const rzp = new window.Razorpay(withRazorpayCheckoutConfig({
          key: key_id,
          amount: amount,
          currency: 'INR',
          name: 'Backyard',
          description: `${info.event_title} - ${role.replace('_', ' ')} Registration`,
          order_id: order_id,
          prefill: buildRazorpayPrefill({ contact: user?.mobile_number, email: user?.email, name: user?.name }),
          handler: async function (response) {
            if (paymentDone) return;
            paymentDone = true;
            stopPolling?.();
            try {
              await submitRegistration({
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Registration failed after payment');
            }
          },
          modal: { ondismiss: () => { stopPolling?.(); setSubmitting(false); } },
          theme: { color: '#2B3A67' },
        }));

        // UPI Collect rescue — see utils/razorpay.js
        stopPolling = pollRazorpayOrderStatus({
          backendUrl,
          orderId: order_id,
          onPaid: async (info) => {
            if (paymentDone) return;
            paymentDone = true;
            try { rzp.close(); } catch { /* already closed */ }
            try {
              await submitRegistration({
                payment_id: info.captured_payment_id,
                signature: '',
              });
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Payment received but registration failed. Contact support.');
            } finally {
              setSubmitting(false);
            }
          },
        });

        rzp.open();
      } else {
        await axiosInstance.post(`/v1/events/${eventId}/register`, {
          role,
          registration_data: formData,
          declaration_confirmed: true,
          declarations,
          razorpay_order_id: '',
          razorpay_payment_id: '',
          razorpay_signature: '',
        });
        toast.success(role === 'player' ? 'Registration confirmed!' : 'Registration submitted for approval!');
        navigate(-1);
      }
    } catch (e) {
      if (await gate.handleError(e)) toast.info('Thanks — tap submit again to finish.');
      else toast.error(e?.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B3A67]" /></div>;

  if (!info) return <div className="text-center py-20 text-gray-400">Event not found</div>;

  const alreadyRegistered = invite
    ? (invite.already_on_team ? 'on this team' : (!invite.can_join ? invite.cannot_join_reason : null))
    : (info.my_roles?.[role] && info.my_roles?.[role] !== 'draft');
  const byoMode = info.team_formation_method === 'team_registration';
  // Only the registrations the organiser opened in the app
  const offered = info.roles_offered || ['player', 'team_owner', 'sponsor'];
  const visibleRoles = invite ? ROLES.filter((r) => r.id === 'player')
    : byoMode ? [TEAM_ROLE, ...ROLES.filter((r) => r.id === 'sponsor' && offered.includes('sponsor'))]
    : ROLES.filter((r) => offered.includes(r.id));
  const isDraft = info.my_roles?.[role] === 'draft';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#2B3A67] text-white px-4 py-4 sticky top-0 z-30 pt-safe">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1" data-testid="back-btn">
            <i className="ph-bold ph-arrow-left text-lg" />
          </button>
          <div>
            <h1 className="font-extrabold text-base leading-tight">{info.event_title}</h1>
            <p className="text-xs text-white/70">Tournament Registration</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* Registration Deadline Notice */}
        {info.registration_closes && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3" data-testid="deadline-notice">
            <i className="ph-bold ph-clock text-blue-500 text-xl mt-0.5"></i>
            <div>
              <p className="font-bold text-blue-800 text-sm">Registration Deadline</p>
              <p className="text-xs text-blue-700 mt-1">{formatDate(info.registration_closes)}</p>
            </div>
          </div>
        )}

        {/* Joining a team through an invite link */}
        {invite && (
          <div className="bg-[#2B3A67] text-white rounded-2xl p-5 shadow-sm" data-testid="invite-banner">
            <p className="text-xs uppercase tracking-widest opacity-70">You're invited to join</p>
            <p className="text-xl font-extrabold mt-1">{invite.team_name}</p>
            <p className="text-xs opacity-80 mt-1">
              Registered by {invite.registered_by} · {invite.players}/{invite.squad_max} players ·{' '}
              {invite.fee_mode === 'per_player' && invite.player_fee > 0
                ? `player fee ₹${(invite.player_fee / 100).toLocaleString('en-IN')}` : 'no fee to join'}
            </p>
            {!invite.can_join && (
              <p className="text-sm font-bold mt-2 text-amber-200">
                {invite.already_on_team ? "You're already on this team." : invite.cannot_join_reason}
              </p>
            )}
          </div>
        )}

        {/* Section 1: Select Role */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="role-selector">
          <SectionTitle icon={<i className="ph-bold ph-user-switch" />}>Select Your Role</SectionTitle>
          <div className="space-y-2">
            {visibleRoles.length === 0 && (
              <p className="text-sm text-gray-500" data-testid="no-roles-open">
                Registration isn't open in the app for this tournament. Contact the organiser to take part.
              </p>
            )}
            {visibleRoles.map(r => (
              <button key={r.id}
                onClick={() => handleRoleChange(r.id)}
                disabled={!invite && info.my_roles?.[r.id] && info.my_roles?.[r.id] !== 'draft'}
                className={`w-full text-left rounded-xl p-3.5 border-2 transition-all ${
                  role === r.id ? 'border-[#2B3A67] bg-[#2B3A67]/5' :
                  (info.my_roles?.[r.id] && info.my_roles?.[r.id] !== 'draft') ? 'border-gray-100 opacity-50 cursor-not-allowed' :
                  'border-gray-100 hover:border-gray-200'
                }`}
                data-testid={`role-${r.id}`}>
                <div className="flex items-center gap-3">
                  <i className={`${r.icon} text-xl ${role === r.id ? 'text-[#2B3A67]' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-bold text-sm">{r.label}</p>
                    <p className="text-xs text-gray-400">{r.desc}</p>
                    {info.my_roles?.[r.id] === 'draft' && <p className="text-xs text-amber-600 font-bold mt-0.5">Draft saved</p>}
                    {info.my_roles?.[r.id] && info.my_roles?.[r.id] !== 'draft' && <p className="text-xs text-green-600 font-bold mt-0.5">Already registered ({info.my_roles[r.id]})</p>}
                  </div>
                  <span className="ml-auto text-xs font-bold text-[#2B3A67]">
                    {r.id === 'team'
                      ? ((info.fees?.team || info.fees?.player) ? 'Fees vary' : 'Free')
                      : feeForRole(r.id) > 0 ? `₹${(feeForRole(r.id) / 100).toLocaleString()}` : 'Free'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Universal-profile details used for this registration (read-only here) */}
        {role && (
          <ProfileSummary user={user} needs={needsFor(role)} ageRestricted={info.age_restricted}
            onEdit={() => gate.ask(missingProfile(user, needsFor(role)), {
              fields: ['profile_picture', 'mobile_number', ...(info.age_restricted ? ['date_of_birth'] : []), 'instagram_handle'],
              reason: 'Changes are saved to your profile.',
            })} />
        )}

        {/* Role-specific form */}
        {role === 'team' && <TeamRegistrationPanel eventId={eventId} info={info} user={user} />}
        {role === 'player' && <PlayerForm data={formData} set={set} setFormData={setFormData} uploadFile={uploadFile}
          fileUploading={fileUploading} info={info} />}
        {role === 'team_owner' && <TeamOwnerForm data={formData} set={set} info={info} user={user} />}
        {role === 'sponsor' && <SponsorForm data={formData} set={set} uploadFile={uploadFile} fileUploading={fileUploading} />}

        {/* Declaration */}
        {role && role !== 'team' && (
          <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="declaration-section">
            <SectionTitle icon={<i className="ph-bold ph-scroll" />}>Final Declaration</SectionTitle>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="declaration-terms-wrapper">
                <input type="checkbox" checked={declarations.accurate_rules_terms}
                  onChange={e => setDeclarations(prev => ({ ...prev, accurate_rules_terms: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#2B3A67] focus:ring-[#2B3A67]"
                  data-testid="declaration-terms" />
                <span className="text-sm text-gray-700">
                  My details are accurate, and I accept the{' '}
                  {info?.rulebook_version ? (
                    <a href={`/tournaments/${eventId}?phase=rules`} target="_blank" rel="noopener noreferrer"
                      className="text-[#2B3A67] font-semibold underline underline-offset-2"
                      data-testid="rulebook-link">
                      tournament rules (version {info.rulebook_version})
                    </a>
                  ) : 'tournament rules'}
                  {' '}and the{' '}
                  {info?.rules_pdf_url ? (
                    <a href={info.rules_pdf_url} target="_blank" rel="noopener noreferrer"
                      className="text-[#2B3A67] font-semibold underline underline-offset-2"
                      data-testid="terms-pdf-link">
                      event terms &amp; conditions
                    </a>
                  ) : (
                    'event terms & conditions'
                  )}
                  {' '}<span className="text-red-500">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={declarations.media_consent}
                  onChange={e => setDeclarations(prev => ({ ...prev, media_consent: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#2B3A67] focus:ring-[#2B3A67]"
                  data-testid="declaration-media" />
                <span className="text-sm text-gray-700">
                  I consent to the use of my image/likeness in media coverage <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {role && role !== 'team' && (
          <div className="space-y-3">
            {/* Save Draft Button */}
            <button
              onClick={handleSaveDraft}
              disabled={savingDraft || submitting || fileUploading}
              className="w-full py-3 bg-gray-100 text-[#2B3A67] font-bold rounded-2xl text-sm disabled:opacity-50 transition-opacity border border-gray-200 hover:bg-gray-200"
              data-testid="save-draft-btn">
              {savingDraft ? 'Saving Draft...' : (
                <>
                  <i className="ph-bold ph-floppy-disk mr-2"></i>
                  Save Draft
                </>
              )}
            </button>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !allDeclared || !!alreadyRegistered || fileUploading}
              className="w-full py-3.5 bg-[#2B3A67] text-white font-extrabold rounded-2xl text-sm disabled:opacity-50 transition-opacity"
              data-testid="submit-registration-btn">
              {submitting ? 'Processing...' :
                invite && alreadyRegistered ? (invite.already_on_team ? "You're on this team" : "Can't join this team") :
                invite ? (feeForRole('player') > 0 ? `Pay ₹${(feeForRole('player') / 100).toLocaleString()} & Join ${invite.team_name}` : `Join ${invite.team_name}`) :
                alreadyRegistered ? `Already registered (${info.my_roles[role]})` :
                role === 'team_owner' ? 'Submit for Approval' :
                feeForRole(role) > 0 ? `Pay ₹${(feeForRole(role) / 100).toLocaleString()} & Register` : 'Submit Registration'}
            </button>

            {user?.review_mode && !alreadyRegistered && !invite && (
              <ReviewModeBanner
                disabled={submitting}
                amountLabel={feeForRole(role) > 0 ? `₹${(feeForRole(role) / 100).toLocaleString()}` : ''}
                onBypass={async () => {
                  if (!allDeclared) {
                    toast.error('Please accept all declarations first.');
                    return;
                  }
                  try {
                    setSubmitting(true);
                    await axiosInstance.post(`/v1/events/${eventId}/register`, {
                      role,
                      registration_data: formData,
                      declaration_confirmed: true,
                      declarations,
                      razorpay_order_id: 'REVIEW_MODE',
                      razorpay_payment_id: 'REVIEW_MODE',
                      razorpay_signature: 'REVIEW_MODE',
                    });
                    toast.success('Registration submitted (App Review Mode).');
                    navigate(-1);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Bypass failed.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
      {gate.sheet}
    </div>
  );
}


// ─── Profile summary (universal profile, read-only on the form) ───────────────

function ProfileSummary({ user, needs, ageRestricted, onEdit }) {
  const missing = missingProfile(user, needs);
  const age = user?.date_of_birth ? calculateAge(user.date_of_birth) : null;
  const row = (label, value, key) => (
    <div className="flex justify-between gap-3 text-sm py-1">
      <span className="text-gray-400">{label}</span>
      <span className={`font-medium text-right ${missing.includes(key) ? 'text-red-500' : 'text-gray-700'}`}>
        {value || (needs.includes(key) ? 'Required' : 'Not set')}
      </span>
    </div>
  );
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="profile-summary">
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-full overflow-hidden flex items-center justify-center shrink-0 ${
          missing.includes('profile_picture') ? 'bg-red-50 border-2 border-red-200' : 'bg-gray-100'}`}>
          {user?.profile_picture_url
            ? <img src={user.profile_picture_url} alt="Profile" className="w-full h-full object-cover" />
            : <i className="ph-bold ph-user text-xl text-gray-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#2B3A67] truncate">{user?.name || '…'}</p>
          <p className="text-xs text-gray-400">From your Backyard profile</p>
        </div>
        <button type="button" onClick={onEdit} className="text-xs font-bold text-[#2B3A67] underline" data-testid="edit-profile-details">
          {missing.length ? 'Add details' : 'Edit'}
        </button>
      </div>
      <div className="mt-3 border-t border-gray-50 pt-2">
        {row('Email', user?.email, 'email')}
        {row('Mobile', user?.mobile_number, 'mobile_number')}
        {ageRestricted && row('Date of birth',
          user?.date_of_birth ? `${formatDate(user.date_of_birth)}${age !== null ? ` (age ${age})` : ''}` : null, 'date_of_birth')}
        <div className="flex justify-between gap-3 text-sm py-1">
          <span className="text-gray-400">Instagram</span>
          {user?.instagram_handle ? (
            <a href={instagramUrl(user.instagram_handle)} target="_blank" rel="noreferrer" className="font-medium text-[#2B3A67]">
              @{user.instagram_handle}
            </a>
          ) : (
            <button type="button" onClick={onEdit} className="text-xs font-bold text-[#2B3A67] underline" data-testid="add-instagram">
              Add (optional)
            </button>
          )}
        </div>
      </div>
      {missing.length > 0 && (
        <p className="text-xs text-red-500 mt-2" data-testid="profile-missing-note">
          <i className="ph-bold ph-warning mr-1" />Add the missing details to register.
        </p>
      )}
    </div>
  );
}


// ─── Player Form ──────────────────────────────────────────────────────────────

function PlayerForm({ data, set, setFormData, uploadFile, fileUploading, info }) {
  const sport = info?.sport || 'cricket';
  const days = eventDays(info?.event_dates);
  const unavailable = Array.isArray(data.unavailable_dates) ? data.unavailable_dates : [];
  const toggleDay = (d) => set('unavailable_dates',
    unavailable.includes(d) ? unavailable.filter((x) => x !== d) : [...unavailable, d].sort());
  const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="space-y-5">
      {/* Sport profile — saved for next time */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="cricket-profile-section">
        <SectionTitle icon={<i className="ph-bold ph-baseball" />}>{sport === 'cricket' ? 'Cricket Profile' : 'Playing Profile'}</SectionTitle>
        <SportProfileFields sport={sport} value={data} inputCls={inputCls} Field={Field}
          onChange={(next) => setFormData(next)} />
        <p className="text-xs text-gray-400 mt-3">
          <i className="ph-bold ph-bookmark-simple mr-1" />
          Saved to your profile, so your next registration is pre-filled.
        </p>
      </div>

      {/* Skill level for this tournament */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="skill-info-section">
        <SectionTitle icon={<i className="ph-bold ph-chart-bar" />}>Skill Category</SectionTitle>
        <Field label="Your category" required>
          <div className="flex gap-2" role="radiogroup" aria-label="Category">
            {['A', 'B', 'C', 'D'].map((c) => (
              <button type="button" key={c} role="radio" aria-checked={data.category === c} onClick={() => set('category', c)}
                className={`w-12 h-11 rounded-xl text-sm font-extrabold border-2 ${
                  data.category === c ? 'border-[#2B3A67] bg-[#2B3A67] text-white' : 'border-gray-100 text-gray-600'}`}
                data-testid={`player-category-${c}`}>
                {c}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">A is the strongest. The organisers may adjust it.</p>
        </Field>
      </div>

      {/* Age-restricted tournaments: ID proof */}
      {info?.age_restricted && (
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="id-proof-section">
          <SectionTitle icon={<i className="ph-bold ph-identification-card" />}>Age Verification</SectionTitle>
          <Field label="ID proof showing your date of birth" required>
            <input type="file" accept="image/*,.pdf"
              onChange={e => e.target.files[0] && uploadFile(e.target.files[0], 'id_proof_url', 'registrations/id_proofs')}
              className="text-sm text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-gray-100 file:text-gray-700"
              data-testid="player-id-upload" disabled={fileUploading} />
            {data.id_proof_url && <p className="text-xs text-green-600 mt-1"><i className="ph-bold ph-check-circle mr-1"></i>Uploaded</p>}
            <p className="text-xs text-gray-400 mt-1">
              This tournament has an age rule{info.age_rule ? ` (${info.age_rule})` : ''}. Seen only by the organisers.
            </p>
          </Field>
        </div>
      )}

      {/* Availability (only when the event runs over more than one day) */}
      {(!days || days.length > 1) && (
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="id-availability-section">
          <SectionTitle icon={<i className="ph-bold ph-calendar-check" />}>Availability</SectionTitle>
          <Field label="Available for all match days?">
            <div className="flex gap-2">
              {['Yes', 'No'].map((v) => (
                <button type="button" key={v} onClick={() => set('available_all_days', v)}
                  className={`px-5 py-2 rounded-xl text-sm font-bold border-2 ${
                    data.available_all_days === v ? 'border-[#2B3A67] bg-[#2B3A67] text-white' : 'border-gray-100 text-gray-600'}`}
                  data-testid={`player-availability-${v.toLowerCase()}`}>
                  {v}
                </button>
              ))}
            </div>
          </Field>
          {data.available_all_days === 'No' && (
            <div className="mt-4">
              <Field label="Days you can't make" required>
                {days ? (
                  <div className="flex flex-wrap gap-2" data-testid="player-unavailable-days">
                    {days.map((d) => (
                      <button type="button" key={d} onClick={() => toggleDay(d)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${
                          unavailable.includes(d) ? 'bg-red-50 border-red-300 text-red-600' : 'border-gray-200 text-gray-600'}`}>
                        {dayLabel(d)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input className={inputCls} value={typeof data.unavailable_dates === 'string' ? data.unavailable_dates : ''}
                    onChange={e => set('unavailable_dates', e.target.value)} placeholder="e.g. 15th April, 20th April"
                    data-testid="player-unavailable-dates" />
                )}
              </Field>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Team Owner Form ──────────────────────────────────────────────────────────

function TeamOwnerForm({ data, set, info, user }) {
  const GST_RATE = 0.18; // 18% GST for Corporate
  const ownerSlotFee = info?.fees?.team_owner || 0;
  const isCorporate = data.ownership_type === 'Corporate';
  const gstAmount = isCorporate ? Math.round(ownerSlotFee * GST_RATE) : 0;
  const totalFee = ownerSlotFee + gstAmount;

  return (
    <div className="space-y-5">
      {/* Owner contact: from the profile unless someone else is the contact */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="owner-details-section">
        <SectionTitle icon={<i className="ph-bold ph-user-circle" />}>Owner Contact</SectionTitle>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={!!data.contact_is_different}
            onChange={e => set('contact_is_different', e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300" data-testid="owner-contact-different" />
          <span className="text-sm text-gray-700">
            Someone else is the owner / contact person
            <span className="block text-xs text-gray-400">Otherwise we use your profile details above.</span>
          </span>
        </label>

        {data.contact_is_different && (
          <div className="space-y-4 mt-4">
            <Field label="Owner Name" required>
              <input className={inputCls} value={data.owner_name || ''} onChange={e => set('owner_name', e.target.value)}
                placeholder="Full name of the team owner" data-testid="owner-name-input" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" required>
                <input type="tel" className={inputCls} value={data.owner_phone || ''} onChange={e => set('owner_phone', e.target.value)}
                  placeholder="+91 9876543210" data-testid="owner-phone-input" />
              </Field>
              <Field label="Email" required>
                <input type="email" className={inputCls} value={data.owner_email || ''} onChange={e => set('owner_email', e.target.value)}
                  placeholder="owner@example.com" data-testid="owner-email-input" />
              </Field>
            </div>
          </div>
        )}

        <div className="space-y-4 mt-4">
          <Field label="Company / Brand Name">
            <input className={inputCls} value={data.company_name || ''} onChange={e => set('company_name', e.target.value)}
              placeholder="Optional - for corporate registrations" data-testid="company-name-input" />
          </Field>
          <Field label="Address">
            <textarea className={inputCls + ' resize-none'} rows={2} value={data.owner_address || ''}
              onChange={e => set('owner_address', e.target.value)} placeholder="Billing address (optional)"
              data-testid="owner-address-input" />
          </Field>
        </div>
      </div>

      {/* Team Information Section */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="team-info-section">
        <SectionTitle icon={<i className="ph-bold ph-trophy" />}>Team Information</SectionTitle>
        
        <Field label="Team Name" required>
          <input 
            className={inputCls} 
            value={data.team_name || ''} 
            onChange={e => set('team_name', e.target.value)} 
            placeholder="e.g. Mumbai Mavericks" 
            data-testid="team-name-input" 
          />
        </Field>
      </div>

      {/* Ownership Details Section */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="ownership-details-section">
        <SectionTitle icon={<i className="ph-bold ph-briefcase" />}>Ownership Details</SectionTitle>
        
        <div className="space-y-4">
          <Field label="Ownership Type" required>
            <select 
              className={selectCls} 
              value={data.ownership_type || ''} 
              onChange={e => set('ownership_type', e.target.value)}
              data-testid="ownership-type-select"
            >
              <option value="">Select...</option>
              <option value="Individual">Individual</option>
              <option value="Corporate">Corporate</option>
            </select>
          </Field>

          {isCorporate && (
            <Field label="GST Number" required>
              <input 
                className={inputCls} 
                value={data.gst_number || ''} 
                onChange={e => set('gst_number', e.target.value.toUpperCase())} 
                placeholder="e.g. 22AAAAA0000A1Z5"
                maxLength={15}
                data-testid="gst-number-input" 
              />
              <p className="text-xs text-gray-400 mt-1">15-character GST Identification Number</p>
            </Field>
          )}

          {/* Fee Display — nothing to pay when the owner fee is 0 */}
          {ownerSlotFee <= 0 ? (
            <div className="bg-green-50 rounded-xl p-4 mt-4 text-sm text-green-700 font-bold" data-testid="owner-no-fee">
              <i className="ph-bold ph-check-circle mr-1"></i>
              No fee for team owners — you're confirmed once the organiser approves you.
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4 mt-4">
              <p className="text-xs font-bold text-gray-500 mb-3">OWNER SLOT FEE BREAKDOWN</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Fee</span>
                  <span className="font-medium">₹{(ownerSlotFee / 100).toLocaleString()}</span>
                </div>
                {isCorporate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="font-medium text-amber-600">+ ₹{(gstAmount / 100).toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between">
                  <span className="font-bold text-gray-800">Total Payable</span>
                  <span className="font-extrabold text-[#2B3A67]">₹{(totalFee / 100).toLocaleString()}</span>
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-3 flex items-center gap-1">
                <i className="ph-bold ph-info"></i>
                Payment will be requested after admin approval
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Auction Participation Section */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="auction-participation-section">
        <SectionTitle icon={<i className="ph-bold ph-gavel" />}>Auction Participation</SectionTitle>
        
        <Field label="Confirm participation in auction?">
          <select 
            className={selectCls} 
            value={data.auction_participation || ''} 
            onChange={e => set('auction_participation', e.target.value)}
            data-testid="auction-participation-select"
          >
            <option value="">Select...</option>
            <option value="Yes">Yes, I will participate in the auction</option>
            <option value="No">No, I will not participate</option>
          </select>
        </Field>
      </div>
    </div>
  );
}


// ─── Sponsor Form ─────────────────────────────────────────────────────────────

function SponsorForm({ data, set, uploadFile, fileUploading }) {
  const brandingOptions = [
    { key: 'logo_on_jerseys', label: 'Logo on jerseys' },
    { key: 'ground_branding', label: 'Ground branding' },
    { key: 'digital_promotion', label: 'Digital promotion' },
    { key: 'social_media', label: 'Social media promotion' },
    { key: 'custom_activation', label: 'Custom activation' },
  ];

  const toggleBranding = (key) => {
    const current = data.branding_requirements || {};
    set('branding_requirements', { ...current, [key]: !current[key] });
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4" data-testid="sponsor-form">
      <SectionTitle icon={<i className="ph-bold ph-handshake" />}>Sponsorship Details</SectionTitle>

      <Field label="Company / Brand Name" required>
        <input className={inputCls} value={data.company_name || ''} onChange={e => set('company_name', e.target.value)}
          placeholder="Who is sponsoring?" data-testid="sponsor-company-name" />
      </Field>

      <Field label="Type of Sponsorship" required>
        <select className={selectCls} value={data.sponsorship_type || ''} onChange={e => set('sponsorship_type', e.target.value)} data-testid="sponsorship-type">
          <option value="">Select...</option>
          <option value="Title">Title</option>
          <option value="Associate">Associate</option>
          <option value="Team">Team</option>
          <option value="Match">Match</option>
          <option value="Custom">Custom</option>
        </select>
      </Field>

      <Field label="Budget Range" required>
        <select className={selectCls} value={data.budget_range || ''} onChange={e => set('budget_range', e.target.value)} data-testid="budget-range">
          <option value="">Select...</option>
          <option value="Under ₹25,000">Under ₹25,000</option>
          <option value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</option>
          <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
          <option value="₹1,00,000 - ₹5,00,000">₹1,00,000 - ₹5,00,000</option>
          <option value="₹5,00,000+">₹5,00,000+</option>
        </select>
      </Field>

      <SectionTitle icon={<i className="ph-bold ph-megaphone" />}>Branding Requirements</SectionTitle>
      <div className="space-y-2">
        {brandingOptions.map(b => (
          <label key={b.key} className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox"
              checked={!!data.branding_requirements?.[b.key]}
              onChange={() => toggleBranding(b.key)}
              className="w-4 h-4 rounded border-gray-300 text-[#2B3A67] focus:ring-[#2B3A67]" />
            <span className="text-sm text-gray-700">{b.label}</span>
          </label>
        ))}
      </div>

      <SectionTitle icon={<i className="ph-bold ph-buildings" />}>Business Details</SectionTitle>
      <Field label="GST Number (Optional)">
        <input className={inputCls} value={data.gst_number || ''} onChange={e => set('gst_number', e.target.value.toUpperCase())}
          placeholder="e.g. 22AAAAA0000A1Z5" maxLength={15} />
      </Field>

      <Field label="Upload Company Logo">
        <input type="file" accept="image/*"
          onChange={e => e.target.files[0] && uploadFile(e.target.files[0], 'company_logo_url', 'registrations/sponsor_logos')}
          className="text-sm text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-gray-100 file:text-gray-700"
          data-testid="company-logo-upload" disabled={fileUploading} />
        {data.company_logo_url && <p className="text-xs text-green-600 mt-1"><i className="ph-bold ph-check-circle mr-1"></i>Uploaded</p>}
      </Field>

      <Field label="Special Requirements / Proposal Notes">
        <textarea className={inputCls} rows={3} value={data.special_requirements || ''} onChange={e => set('special_requirements', e.target.value)} placeholder="Any special activation requirements or notes..." />
      </Field>
    </div>
  );
}
